import axios from "axios";
import axiosRetry from "axios-retry";
import { Alert, Platform } from "react-native";
import baseURL from "./baseurl";

let api = axios.create({
  baseURL,
  headers: {
    "Content-type": "application/json",
    Accept: "application/json",
  },
});

// Configure axios-retry to exclude 401 errors and only retry on network/server errors
axiosRetry(api, {
  retries: 3,
  retryDelay: (retryCount) => {
    console.log(`Retry attempt: ${retryCount}`);
    return retryCount * 1000; // Exponential backoff
  },
  retryCondition: (error) => {
    // Don't retry on 401 (authentication) errors
    const status = error.response?.status;
    if (status === 401) {
      return false;
    }
    // Retry on network errors or 5xx errors
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || (status >= 500 && status < 600);
  },
  shouldResetTimeout: true,
});

// Track if we're currently handling a 401 to prevent multiple logouts
let isHandling401 = false;
let interceptorId = null;
// Track retry attempts per request to prevent infinite loops
let retryAttemptsMap = new WeakMap();
const MAX_401_ATTEMPTS = 3;

let interceptor = (onUnauthenticated, loggedIn) => {
  // Clear any existing interceptor
  if (interceptorId !== null) {
    api.interceptors.response.eject(interceptorId);
  }

  const onRequestSuccess = (config) => {
    return config;
  };
  const onRequestFail = (error) => {
    return Promise.reject(error);
  };
  api.interceptors.request.use(onRequestSuccess, onRequestFail);

  const onResponseSuccess = (response) => {
    // Reset the flag on successful responses
    isHandling401 = false;
    // Clear retry attempts for successful requests
    if (response.config) {
      retryAttemptsMap.delete(response.config);
    }
    return response;
  };

  const onResponseFail = (error) => {
    const status = error.status || error.response?.status;
    const isAuthEndpoint = error.config?.url?.includes('/auth/');

    // Handle 401 Unauthorized - but not for auth endpoints themselves
    if (status === 401 && loggedIn && !isAuthEndpoint) {
      // Track retry attempts per request
      const currentAttempts = retryAttemptsMap.get(error.config) || 0;

      // Check if we've exceeded max attempts for this specific request
      if (currentAttempts >= MAX_401_ATTEMPTS) {
        console.log('Max 401 retry attempts reached, stopping further attempts');
        retryAttemptsMap.delete(error.config);
        return Promise.reject(error);
      }

      // Increment retry counter for this request
      retryAttemptsMap.set(error.config, currentAttempts + 1);

      // Prevent multiple simultaneous 401 handling across all requests
      if (!isHandling401) {
        isHandling401 = true;

        // Clear the Authorization header immediately
        delete api.defaults.headers.common.Authorization;

        // Show alert only once
        if (currentAttempts === 0) {
          if (Platform.OS === "web") {
            // alert("Your session has expired. Please login again.");
          } else {
            // Alert.alert(
            //   "Session Expired",
            //   "Your session has expired. Please login again.",
            //   [{ text: "OK", style: "default" }],
            //   { cancelable: false }
            // );
          }
        }

        // Call the unauthorized handler
        onUnauthenticated();

        // Reset the flag after a delay
        setTimeout(() => {
          isHandling401 = false;
          // Clear all retry attempts after logout
          retryAttemptsMap = new WeakMap();
        }, 2000);
      }

      // Reject the promise to stop further processing
      return Promise.reject(error);
    }

    // Server errors (5xx) are handled by axios-retry, no need for alerts here
    // They will be retried automatically up to 3 times

    return Promise.reject(error);
  };

  // Store the interceptor ID for cleanup
  interceptorId = api.interceptors.response.use(onResponseSuccess, onResponseFail);
};

export { interceptor };

export default api;
